import {
  buildDailyHistoryFromDetails,
  buildLatestDailyHistory,
} from '@/features/shared/components/entityDailyHistory/dailyHistoryRows';
import {
  resolvePreferredCiDisplayStatus,
  resolveStageDisplayStatus,
} from '@/features/shared/utils/ciStatus';
import { buildJobDetailPath, buildJobRunDetailPath } from '../../jobs/utils/jobPaths';
import { resolvePipelineSlug } from '../utils/pipelineSlug';
import { buildStageDetailPath, buildStageExecutionDetailPath } from '../utils/stagePaths';
import type { EntityDailyHistoryRow } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import type { DailyHistoryCell } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';
import type {
  JobListItemDto,
  PipelineStageExecutionListItemDto,
  PipelineStageListItemDto,
} from '@flowqa/shared';

type StageHistoryExecution = Pick<
  PipelineStageExecutionListItemDto,
  | 'id'
  | 'status'
  | 'lifecycleState'
  | 'conclusion'
  | 'requiresManualAction'
  | 'buildKey'
  | 'lastUpdatedAt'
  | 'finishedAt'
  | 'startedAt'
>;

const resolveStageHistoryExecutionStatus = (
  execution: StageHistoryExecution,
): NonNullable<DailyHistoryCell['status']> =>
  resolvePreferredCiDisplayStatus({
    status: execution.status,
    lifecycleState: execution.lifecycleState,
    conclusion: execution.conclusion,
    requiresManualAction: execution.requiresManualAction,
  }) ?? execution.status;

const buildStageLatestDailyHistory = (stage: PipelineStageListItemDto): DailyHistoryCell[] => {
  const latestRun = stage.latestRun;
  if (latestRun?.timestamp == null) {
    return [];
  }

  const displayStatus = resolveStageDisplayStatus(stage) ?? latestRun.status;

  return buildLatestDailyHistory({
    timestamp: latestRun.timestamp,
    status: displayStatus,
    detail: {
      status: displayStatus,
      timestamp: latestRun.timestamp,
      runLabel: stage.latestRunBuildKey ?? null,
      runHref: buildStageExecutionDetailPath({
        stageExecutionId: latestRun.id,
        pipelineId: stage.pipelineId,
        stageName: stage.stageName,
      }),
    },
  });
};

const buildDailyHistoryFromExecutions = (
  stage: PipelineStageListItemDto,
  stageExecutions: readonly StageHistoryExecution[] | undefined,
): DailyHistoryCell[] => {
  const historyDetails = (stageExecutions ?? [])
    .map((execution) => {
      const timestamp =
        execution.lastUpdatedAt ?? execution.finishedAt ?? execution.startedAt ?? null;
      if (!timestamp) {
        return null;
      }

      return {
        status: resolveStageHistoryExecutionStatus(execution),
        timestamp,
        runLabel: execution.buildKey ?? null,
        runHref: buildStageExecutionDetailPath({
          stageExecutionId: execution.id,
          pipelineId: stage.pipelineId,
          stageName: stage.stageName,
        }),
      };
    })
    .filter(
      (
        detail,
      ): detail is {
        status: NonNullable<DailyHistoryCell['status']>;
        timestamp: Date;
        runLabel: string | null;
        runHref: string;
      } => detail !== null,
    );

  return buildDailyHistoryFromDetails({
    items: historyDetails,
    getDay: (item) => item.timestamp,
    getStatus: (item) => item.status,
  });
};

const buildJobLatestDailyHistory = (job: JobListItemDto): DailyHistoryCell[] => {
  if (job.latestRun?.timestamp == null) {
    return [];
  }

  return buildLatestDailyHistory({
    timestamp: job.latestRun.timestamp,
    status: job.latestRun.status,
    detail: {
      status: job.latestRun.status,
      timestamp: job.latestRun.timestamp,
      runLabel: job.latestRunBuildKey ?? null,
      runHref: buildJobRunDetailPath({
        jobRunId: job.latestRun.id,
        pipelineId: job.pipelineId,
        stageName: job.stageName,
        jobName: job.jobName,
      }),
    },
  });
};

const buildJobDailyHistory = (job: JobListItemDto): DailyHistoryCell[] => {
  const historyDetails = (job.runSummaries ?? [])
    .filter(
      (
        run,
      ): run is NonNullable<JobListItemDto['runSummaries']>[number] & {
        timestamp: Date;
      } => run.timestamp instanceof Date,
    )
    .map((run) => ({
      status: run.status,
      timestamp: run.timestamp,
      runLabel: run.buildKey ?? null,
      runHref: buildJobRunDetailPath({
        jobRunId: run.id,
        pipelineId: job.pipelineId,
        stageName: job.stageName,
        jobName: job.jobName,
      }),
    }));

  return buildDailyHistoryFromDetails({
    items: historyDetails,
    getDay: (item) => item.timestamp,
    getStatus: (item) => item.status,
  });
};

const buildStageJobChildRows = (
  jobs: readonly JobListItemDto[] | undefined,
  isLatestMode: boolean,
): EntityDailyHistoryRow[] =>
  [...(jobs ?? [])]
    .sort((left, right) =>
      left.jobName.localeCompare(right.jobName, undefined, { sensitivity: 'base' }),
    )
    .map((job) => ({
      id: job.id,
      name: job.jobName,
      href: buildJobDetailPath(job.id),
      dailyHistory: isLatestMode ? buildJobLatestDailyHistory(job) : buildJobDailyHistory(job),
    }));

export const buildStageHistoryRows = (
  stages: PipelineStageListItemDto[] | undefined,
  stageExecutionsByStageId: ReadonlyMap<string, readonly StageHistoryExecution[]>,
  jobsByStageId: ReadonlyMap<string, readonly JobListItemDto[]>,
  isLatestMode: boolean,
  options: { loadingChildStageIds?: ReadonlySet<string> } = {},
): EntityDailyHistoryRow[] =>
  (stages ?? []).map((stage) => {
    const dailyHistory = isLatestMode
      ? buildStageLatestDailyHistory(stage)
      : buildDailyHistoryFromExecutions(stage, stageExecutionsByStageId.get(stage.id));
    const prefixLabel = stage.pipelineName ?? null;
    const prefixHref =
      stage.pipelineId && stage.pipelineName
        ? `/pipelines/${resolvePipelineSlug({ id: stage.pipelineId, name: stage.pipelineName })}`
        : null;
    const childrenLoading = options.loadingChildStageIds?.has(stage.id) ? true : undefined;

    return {
      id: stage.id,
      prefixLabel,
      prefixHref,
      name: stage.stageName,
      href: buildStageDetailPath(stage.id),
      dailyHistory,
      hasExpandableChildren: stage.jobCount > 0,
      childrenLoading,
      childrenLoadingRowCount: childrenLoading
        ? Math.max(1, Math.min(stage.jobCount, 3))
        : undefined,
      childRows: buildStageJobChildRows(jobsByStageId.get(stage.id), isLatestMode),
    };
  });
